import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ConsentState = {
  version: string;
  analytics: boolean;
  ads: boolean;
  updatedAt: string;
};

export const CONSENT_STORAGE_KEY = 'noisyuser_consent_v1';
export const CONSENT_VERSION = 'v1';

@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly stateSubject = new BehaviorSubject<ConsentState | null>(this.loadState());
  readonly state$ = this.stateSubject.asObservable();

  hasStoredConsent(): boolean {
    return this.stateSubject.value !== null;
  }

  getState(): ConsentState | null {
    return this.stateSubject.value;
  }

  hasAnalyticsConsent(): boolean {
    return this.stateSubject.value?.analytics ?? false;
  }

  hasAdsConsent(): boolean {
    return this.stateSubject.value?.ads ?? false;
  }

  acceptAll(): void {
    this.setState({ analytics: true, ads: true });
  }

  rejectAll(): void {
    this.setState({ analytics: false, ads: false });
  }

  saveCustom(analytics: boolean, ads: boolean): void {
    this.setState({ analytics, ads });
  }

  clear(): void {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    this.stateSubject.next(null);
  }

  private setState(options: { analytics: boolean; ads: boolean }): void {
    const nextState: ConsentState = {
      version: CONSENT_VERSION,
      analytics: options.analytics,
      ads: options.ads,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(nextState));
    this.stateSubject.next(nextState);
  }

  private loadState(): ConsentState | null {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as ConsentState;
      if (parsed?.version !== CONSENT_VERSION) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
